import {
  StandardTile,
  StandardTileContents,
  StandardTileDescription,
  StandardTileImage,
  StandardTileName,
  StandardTileOverlay,
  StandardTilePrice,
  StandardTileSeparatelyClickableContents,
  useScreenClass,
  Typography,
  FlexBox,
  BasicResponsiveImage,
  RatingsStars,
} from '@vp/swan'
import { useStyles } from '@vp/ubik-context'
import { CouponCallout } from '@vp/coupon-callout'
import { ProductFavoriteHeart } from '@vp/favorites-heart'
import { PriceDisplay } from '@vp/price-platform'

import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { stylesheet } from '../styles/ProductTile.scss'
import { trackClickEvent } from '../utils/tracking/tracking'
import { useAppContext } from '../state/AppContext'
import { reviewData } from '../types/Contentful/ContentfulTypes'
import { ProductTileColorSwatches } from './ProductTileColorSwatches'
import { LinkWrapper } from './LinkWrapper'

import { MSXAttibuteValue } from '../types/Msx'
import BulletPointsDescription from './BulletPointsDescription'
import { showProductHeart } from '../utils/ProductTile/productTile'
import { CalloutWrapper } from './CalloutWrapper'
import { ProductCallout, ProductImage } from './AutoGenerate/types/AutomatedExperience'

export interface ProductTileV2Props {
  name: string
  description?: string
  formattedDescription?: string
  image?: ProductImage
  linkUrl?: string
  callout?: ProductCallout | ProductCallout[]
  review?: reviewData
  layout: 'vertical' | 'horizontal'
  disabled: boolean
  index: number
  pageZone: string
  mpvId?: string
  productId?: string
  imageRef?: React.RefObject<HTMLImageElement>
  showPrice: boolean
  enableCampaignCallouts: boolean
  colorInformation?: MSXAttibuteValue[]
  colorSwatchCount: number
  version?: number
  selectedProductVariants?: Record<string, string>
}

export const ProductTileV2: React.FC<ProductTileV2Props> = (props: ProductTileV2Props) => {
  const { merchandisingCategoryId, auth } = useAppContext()
  const {
    name,
    description,
    formattedDescription,
    linkUrl,
    image,
    callout,
    layout,
    disabled,
    index,
    pageZone,
    mpvId,
    productId,
    imageRef,
    showPrice,
    enableCampaignCallouts,
    review,
    colorInformation,
    colorSwatchCount,
    version,
    selectedProductVariants,
  } = props
  useStyles(stylesheet)
  const lines = description?.split('\n')
  const isMobile = useScreenClass() === 'xs'
  const [isAuthReady, setIsAuthReady] = useState(false)

  useEffect(() => {
    if (auth && auth.getIdentity && auth.getIdentity()?.accessToken) {
      setIsAuthReady(true)
    }
  }, [auth])

  return (
    <StandardTile
      className={classNames({
        'tile-disabled': disabled,
      })}
      layout={layout}
      mb={isMobile && layout !== 'vertical' ? 0 : '5'}
      imageWidth="standard"
    >
      <StandardTileOverlay>
        <LinkWrapper
          href={linkUrl}
          skin="unstyled"
          data-section={pageZone}
          data-position={index}
          data-translation={mpvId}
          onClick={() => {
            trackClickEvent({
              categoryId: merchandisingCategoryId,
              pageZone,
              ctaValue: mpvId || name,
              destinationUrl: linkUrl!,
              index,
            })
          }}
        >
          {callout && <CalloutWrapper callout={callout} enableCampaignCallouts={enableCampaignCallouts} mpvId={mpvId!} />}
        </LinkWrapper>
        {showProductHeart(productId, version, name, mpvId, isAuthReady) && (
          <StandardTileSeparatelyClickableContents onClick={(event) => event.stopPropagation()}>
            <ProductFavoriteHeart
              productKey={productId!}
              productVersion={version!}
              selectedOptions={{}}
              productName={name!}
              mpvId={mpvId!}
              pageZone={pageZone}
              productPosition={index}
            />
          </StandardTileSeparatelyClickableContents>
        )}
      </StandardTileOverlay>

      <StandardTileImage
        className={classNames({
          'tile-disabled-image': disabled,
        })}
      >
        <LinkWrapper
          href={linkUrl}
          skin="unstyled"
          data-section={pageZone}
          data-position={index}
          data-translation={mpvId}
          onClick={() => {
            trackClickEvent({
              categoryId: merchandisingCategoryId,
              pageZone,
              ctaValue: mpvId || name,
              destinationUrl: linkUrl!,
              index,
            })
          }}
        >
          <BasicResponsiveImage aspectRatio={1} src={image?.src} ref={imageRef} alt={image?.altTextOverride || ''} />
        </LinkWrapper>
      </StandardTileImage>

      <StandardTileContents
        className={classNames({
          'tile-disabled-contents': disabled,
        })}
      >
        <LinkWrapper
          href={linkUrl}
          skin="unstyled"
          data-section={pageZone}
          data-position={index}
          data-translation={mpvId}
          onClick={() => {
            trackClickEvent({
              categoryId: merchandisingCategoryId,
              pageZone,
              ctaValue: mpvId || name,
              destinationUrl: linkUrl!,
              index,
            })
          }}
        >
          {colorInformation && colorInformation.length > 1 && (
            <ProductTileColorSwatches colorInformation={colorInformation} colorSwatchCount={colorSwatchCount} />
          )}
          <StandardTileName>{name}</StandardTileName>
          {formattedDescription ? (
            <StandardTileDescription dangerouslySetInnerHTML={{ __html: formattedDescription }} />
          ) : (
            <StandardTileDescription>
              <BulletPointsDescription text={lines} />
            </StandardTileDescription>
          )}
          {review && (
            <FlexBox alignItems="center" flexWrap="wrap" flexDirection="row">
              <RatingsStars ratingValue={review.averageRating} aria-label={review.reviewedByNCustomersLabel} />
              <Typography textColor="subtle" paddingLeft="2">
                ({review.reviewsCount})
              </Typography>
            </FlexBox>
          )}
          {showPrice && mpvId && (
            <StandardTilePrice>
              <PriceDisplay mpvId={mpvId} quantity={Number(selectedProductVariants?.Quantity)} selections={selectedProductVariants} />
            </StandardTilePrice>
          )}
        </LinkWrapper>
        {mpvId && (
          <StandardTileSeparatelyClickableContents>
            <CouponCallout mpvId={mpvId} />
          </StandardTileSeparatelyClickableContents>
        )}
      </StandardTileContents>
    </StandardTile>
  )
}
